/* eslint-disable max-nested-callbacks, max-lines, max-statements, require-jsdoc */
// TODO we have to move all of this validations code from this file to libs
import isLength from 'validator/lib/isLength';
import isEmail from 'validator/lib/isEmail';
import isNumeric from 'validator/lib/isNumeric';
import matches from 'validator/lib/matches';
import isEmpty from 'validator/lib/isEmpty';
import creditCardType from 'credit-card-type';

import {ShippingMethodType, ValidationSchemeAddressType} from 'constants/flow/flowTypesCheckout';
import {
    AuthorizationErrorsValidationType,
    AuthorizationType,
    PaymentErrorsType,
    PaymentErrorsValidationType,
    PaymentType,
} from 'constants/flow/flowTypes';

import {PAYMENT_METHODS} from './checkout/paymentMethods';
import {sendHashedEmailEvent} from '@optimaxdev/analytics/mobile';

export const SUCCESS_VALIDATION_RESULT = 'success';

/**
 * Return validation scheme
 * @param {Object} exclude The rules which should be excluded
 * @return {Object} rules Validation scheme
 */
export function makeValidationScheme(
    exclude: {[field: string]: boolean} = {},
): ValidationSchemeAddressType {
    const rules = {
        firstName: (value: string): string => {
            if (!isLength(value.trim(), {min: 1})) return 'This is a required field';
            if (isNumeric(value.trim())) return 'Invalid field';
            return SUCCESS_VALIDATION_RESULT;
        },
        lastName: (value: string): string => {
            if (!isLength(value.trim(), {min: 1, max: 40})) return 'This is a required field';
            if (isNumeric(value.trim())) return 'Invalid field';
            return SUCCESS_VALIDATION_RESULT;
        },
        aptSuiteOther: (value: string): string => {
            if (!isLength(value.trim(), {min: 1})) return '';
            return SUCCESS_VALIDATION_RESULT;
        },
        city: (value: string): string =>
            isLength(value, {min: 1}) ? SUCCESS_VALIDATION_RESULT : 'This is a required field',
        zipCode: (value: string): string => {
            if (!isLength(value.trim(), {min: 1})) return 'This is a required field';
            if (!matches(value.trim(), /^[a-z0-9-\s]*$/i)) return 'Invalid field';
            return SUCCESS_VALIDATION_RESULT;
        },
        phone: (value: string): string => {
            if (!isLength(value.trim(), {min: 1})) return 'This is a required field';
            if (!matches(value.trim(), /[\d+()-]/gim)) return 'Invalid field';
            return SUCCESS_VALIDATION_RESULT;
        },
        email: (value: string): string => {
            if (!isLength(value.trim(), {min: 1})) return 'This is a required field';
            if (!isEmail(value)) return 'Invalid field';

            sendHashedEmailEvent(value);

            return SUCCESS_VALIDATION_RESULT;
        },
        street: (value: string) =>
            isLength(value, {min: 1}) ? SUCCESS_VALIDATION_RESULT : 'This is a required field',
        region: (value: string): string => {
            if (!isLength(value.trim(), {min: 1})) return '';
            return SUCCESS_VALIDATION_RESULT;
        },
        regionCode: (value: string) =>
            isLength(value, {min: 1}) ? SUCCESS_VALIDATION_RESULT : 'This is a required field',
        country: (value: string) =>
            isLength(value, {min: 1}) ? SUCCESS_VALIDATION_RESULT : 'This is a required field',
    };

    if (Object.keys(exclude).length > 0) {
        Object.keys(exclude).forEach((value: string) => {
            if (exclude[value] === false) delete rules[value];
        });
    }

    return rules;
}

/**
 * Validation for card payments
 */
export class ValidationCreditCard {
    fields: PaymentErrorsType;
    rules: PaymentErrorsValidationType;

    /**
     * Constructor
     * @param {PaymentType} fields all payment fields
     */
    constructor(fields: PaymentType) {
        this.rules = {
            cardNumber: (value: string): string => {
                if (value.trim().length === 0) {
                    return 'This is a required field';
                }
                const val = value.replace(/\s+/g, '').replace(/-/g, '');
                const visaCards = creditCardType(val);
                // @ts-ignore TODO Mikhail check usage and fix code
                if (!isLength(value, {min: visaCards[0].lengths[0]})) {
                    return 'Invalid field';
                }
                return SUCCESS_VALIDATION_RESULT;
            },
            cvv: (value: string): string => {
                if (value.trim().length === 0) {
                    return 'This is a required field';
                }
                if (!isLength(value.trim(), {min: 3, max: 4})) return 'Invalid field';
                return SUCCESS_VALIDATION_RESULT;
            },
            month: (value: string): string => {
                if (value.trim().length === 0) return 'This is a required field';
                if (this.fields.year) {
                    const year = parseInt(`20${this.fields.year}`, 10);
                    if (new Date().getTime() > new Date(year, parseInt(value, 10)).getTime()) {
                        return 'Invalid field';
                    }
                }
                return SUCCESS_VALIDATION_RESULT;
            },
            year: (value: string): string => {
                if (value.trim().length === 0) return 'This is a required field';
                if (this.fields.month) {
                    if (
                        new Date().getTime() >
                        new Date(
                            parseInt(`20${value}`, 10),
                            parseInt(this.fields.month, 10),
                        ).getTime()
                    ) {
                        return 'Invalid field';
                    }
                }
                return SUCCESS_VALIDATION_RESULT;
            },
        };
        this.fields = fields;
    }

    /**
     * Validate one field(needed for input,select etc components)
     * @param {string} field - field
     * @returns {boolean} result validation
     */
    one = (field: string): string => this.rules[field](this.fields[field]);

    /**
     * Validate all fields
     * @returns {PaymentErrorsType} result validations
     */
    all = (): any => {
        const result = {};
        Object.keys(this.fields).map(
            (value: string) => (result[value] = this.rules[value](this.fields[value])),
        );
        return result;
    };
}

/**
 * Validation for authorization forgot
 */
export class ValidationAuthorizationLogin {
    fields: {
        email: string;
        password: string;
    };
    rules: {
        email: (value: string) => string;
        password: (value: string) => string;
    };

    /**
     * Constructor
     * @param {PaymentType} fields all payment fields
     */
    constructor(fields: {email: string; password: string}) {
        this.rules = {
            email: (value: string): string => {
                if (!isLength(value.trim(), {min: 1})) return 'Required';
                if (!isEmail(value)) return 'Invalid field';
                return SUCCESS_VALIDATION_RESULT;
            },
            password: (value: string): string => {
                if (!isLength(value.trim(), {min: 6})) return 'Required';
                return SUCCESS_VALIDATION_RESULT;
            },
        };
        this.fields = fields;
    }

    /**
     * Validate one field(needed for input,select etc components)
     * @param {string} field - field
     * @returns {boolean} result validation
     */
    one = (field: string): string => this.rules[field](this.fields[field]);
}

/**
 * Validation for authorization register
 */
export class ValidationAuthorizationRegister {
    fields: AuthorizationType;
    rules: AuthorizationErrorsValidationType;

    /**
     * Constructor
     * @param {PaymentType} fields all payment fields
     */
    constructor(fields: AuthorizationType) {
        this.rules = {
            firstName: (value: string): string => {
                if (!isLength(value.trim(), {min: 1, max: 40})) return 'Required';
                if (isNumeric(value.trim())) return 'Invalid field';
                return SUCCESS_VALIDATION_RESULT;
            },
            lastName: (value: string): string => {
                if (!isLength(value.trim(), {min: 1, max: 40})) return 'Required';
                if (isNumeric(value.trim())) return 'Invalid field';
                return SUCCESS_VALIDATION_RESULT;
            },
            email: (value: string): string => {
                if (!isLength(value.trim(), {min: 1})) return 'Required';
                if (!isEmail(value)) return 'Invalid field';
                return SUCCESS_VALIDATION_RESULT;
            },
            password: (value: string): string => {
                if (!isLength(value.trim(), {min: 6})) return 'Required';
                return SUCCESS_VALIDATION_RESULT;
            },
            confirmation: (value: string): string => {
                if (isEmpty(value)) return 'Invalid field';
                if (this.fields.password !== value) return 'Invalid field';
                return SUCCESS_VALIDATION_RESULT;
            },
        };
        this.fields = fields;
    }

    /**
     * Validate one field(needed for input,select etc components)
     * @param {string} field - field
     * @returns {boolean} result validation
     */
    one = (field: string): string => this.rules[field](this.fields[field]);

    /**
     * Validate all fields
     * @returns {PaymentErrorsType} result validations
     */
    all = (): any => {
        const result = {};
        Object.keys(this.fields).map(
            (value: string) => (result[value] = this.rules[value](this.fields[value])),
        );
        return result;
    };
}

/**
 * Validation for authorization forgot
 */
export class ValidationAuthorizationForgot {
    fields: {email: string};
    rules: {email: (value: string) => string};

    /**
     * Constructor
     * @param {PaymentType} fields all payment fields
     */
    constructor(fields: {email: string}) {
        this.rules = {
            email: (value: string): string => {
                if (!isLength(value.trim(), {min: 1})) return 'Required';
                if (!isEmail(value)) return 'Invalid field';
                return SUCCESS_VALIDATION_RESULT;
            },
        };
        this.fields = fields;
    }

    /**
     * Validate one field(needed for input,select etc components)
     * @param {string} field - field
     * @returns {boolean} result validation
     */
    one = (field: string): string => this.rules[field](this.fields[field]);
}

/**
 * Get validity of shipping method
 * @param {ShippingMethodType} shippingMethod - current shipping method
 * @returns {string} - success if it valid or error message if not.
 */
export const validateShippingMethod = (shippingMethod: ShippingMethodType): string => {
    if (typeof shippingMethod.code === 'undefined') {
        return 'Please specify shipping method';
    }

    return SUCCESS_VALIDATION_RESULT;
};

export const CHECKOUT_TYPES = {
    AMAZON: 'amazon',
    DEFAULT: 'default',
};

export const CHECKOUT_PAYMENT_TYPES = {
    [CHECKOUT_TYPES.AMAZON]: PAYMENT_METHODS.AMAZON,
};

export const SUCCESS_REDIRECT = '/checkout/onepage/success';

export const CHECKOUT_STEPS = {
    SHIPPING: 0,
    PAYMENT_METHOD: 1,
    ORDER_REVIEW: 2,
};
